/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { EntityState } from '@reduxjs/toolkit';
import type {
  SystemScope,
  ServiceInterface,
  TimeSeriesService,
} from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import type {
  Parameter,
  PlotPreset,
  TimeSeriesLocation,
} from '../components/TimeSeries/types';

export enum TimeSeriesActionOrigin {
  timeSeriesManager = 'timeSeriesManager',
  timeSeriesSelect = 'timeSeriesSelect',
}

export interface Plot {
  title: string;
  plotId: string;
}

export type PopupVariant = 'edit' | 'add' | 'save' | 'show';

export interface ServiceFilterChipsObject {
  serviceId: string;
  serviceName?: string;
  serviceUrl: string;
  type: ServiceInterface;
  enabled?: boolean;
  scope?: SystemScope;
  isLoading?: boolean;
}

export type ServiceFilterChipsType = EntityState<
  ServiceFilterChipsObject,
  string
>;
export type ServiceFilterChipsObjectEntities = Record<
  string,
  ServiceFilterChipsObject
>;

export interface TimeseriesSelectFilters {
  searchFilter: string;
  serviceFilterChips: ServiceFilterChipsType;
  allServiceFilterChipsEnabled: boolean;
}

export interface TimeSeriesServicePopupObject {
  isOpen: boolean;
  variant: PopupVariant;
  url?: string;
  serviceId?: string;
}

export interface TimeseriesSelectStore {
  filters: TimeseriesSelectFilters;
  collectionSelection?: CollectionSelectionTarget;
  currentParameterInfo?: string | Parameter;
  servicePopup: TimeSeriesServicePopupObject;
}

export interface CollectionSelectionTarget {
  plotId: string;
  serviceId: string;
  collectionId: string;
}

export interface TimeseriesLocationData {
  isMapPinDisabled?: boolean;
  shouldHideLocationMarkers?: boolean;
  isLegendVisible?: boolean;
  currentLocation?: TimeSeriesLocation | undefined;
  locationMenuSize?: { width: number; height: number };
  locationMenuPosition?: { x: number; y: number };
  isLocationsDialogOpen?: boolean;
}

export interface TimeSeriesStoreType {
  plotPreset?: PlotPreset;
  timeseriesSelect?: TimeseriesSelectStore;
  services?: TimeSeriesService[];
  locationData?: TimeseriesLocationData;
}

export interface TimeSeriesState {
  byId: Record<string, TimeSeriesStoreType>;
  services: TimeSeriesService[];
  /** Increments whenever any panel's preset is registered; triggers dialog-state restore in LocationSelectDialogConnect. */
  locationDialogRestoreVersion?: number;
}

export interface TimeSeriesPreset {
  plotPreset: PlotPreset;
  services: TimeSeriesService[];
  syncGroupsIds: string[];
  locationMenuSize?: { width: number; height: number };
  locationMenuPosition?: { x: number; y: number };
  isLocationsDialogOpen?: boolean;
}

export interface TimeSeriesPayload {
  panelId?: string;
  origin?: string;
}

export interface RegisterTimeSeriesPresetPayload extends TimeSeriesPayload {
  plotPreset: PlotPreset;
  services?: TimeSeriesService[];
  locationMenuSize?: { width: number; height: number };
  locationMenuPosition?: { x: number; y: number };
  isLocationsDialogOpen?: boolean;
}

export interface ParameterManipulationPayload extends TimeSeriesPayload {
  parameter: Parameter;
}

export interface ParameterPatchPayload extends TimeSeriesPayload {
  parameter: Partial<Parameter>;
}

export interface ParameterActionPayload extends TimeSeriesPayload {
  parameterId: string;
}

export interface PlotActionPayload extends TimeSeriesPayload {
  plotId: string;
}

export interface DuplicatePlotPayload extends PlotActionPayload {
  title: string;
}

export interface AddPlotPayload extends TimeSeriesPayload {
  title: string;
  plotId?: string;
}

export interface SetPlotIsOpenPayload extends PlotActionPayload {
  isOpen: boolean;
}

export interface SetPlotSizePayload extends TimeSeriesPayload {
  plotId: string;
  size: number;
}

export interface UpdateTitlePayload extends TimeSeriesPayload {
  plotId: string;
  title: string;
}

export interface TimeSeriesModuleState {
  timeSeries?: TimeSeriesState;
}

export type TimeSeriesRootState = CoreAppStore & TimeSeriesModuleState;

export interface SetSearchFilterPayload {
  panelId?: string;
  filterText: string;
}

export interface MovePlotPayload extends TimeSeriesPayload {
  oldIndex: number;
  newIndex: number;
}

export interface ReorderPlotsPayload extends TimeSeriesPayload {
  newOrder: string[];
}

export interface MoveParameterPayload extends TimeSeriesPayload {
  oldIndex: number;
  newIndex: number;
  plotId: string;
  toPlotId: string;
}

export interface SetCurrentParameterInfo {
  panelId?: string;
  parameter: string | Parameter;
}

export interface SetCurrentFrozenParameterInfo {
  panelId?: string;
  parameter: Parameter;
}
export interface SetTimeSeriesServicesPayload {
  panelId?: string;
  timeSeriesServices: TimeSeriesService[];
}

export interface RemoveServiceFilterChipFromStorePayload {
  id: string;
}

export interface RemoveTimeSeriesServicePayload {
  panelId?: string;
  id: string;
}

export interface UpsertTimeSeriesServicePayload extends TimeSeriesService {
  panelId?: string;
  isUpdate: boolean;
}

export interface SetTimeSeriesServicePopupPayload {
  panelId?: string;
  url?: string;
  variant: PopupVariant;
  serviceId?: string;
  isOpen: boolean;
}

export interface ToggleDisableMapPinPayload {
  panelId?: string;
  isMapPinDisabled: boolean;
}

export interface ToggleLocationMarkersPayload {
  panelId?: string;
  shouldHideLocationMarkers: boolean;
}

export interface ToggleTimeSeriesLegendPayload {
  panelId?: string;
  isLegendVisible: boolean;
}

export interface SetTimeSeriesLocationPayload {
  panelId?: string;
  location: TimeSeriesLocation | undefined;
}

export interface SetLocationMenuSizeAndPositionPayload extends TimeSeriesPayload {
  size: { width: number; height: number };
  position?: { x: number; y: number };
}

export interface SetLocationsDialogOpenPayload extends TimeSeriesPayload {
  isLocationsDialogOpen: boolean;
}
